import { useState, useCallback } from 'react';

import { PinIcon, PinOffIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PinFilledIcon } from '@/components/atoms/Icons/PinFilledIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AIAgent } from '@/shared/types/brain';

interface FavoriteCellProps {
  aiAgent: AIAgent;
  isFavorite: boolean;
  toggleFavorite: (event: React.MouseEvent<HTMLElement>, aiAgentId: string) => void;
}

export function FavoriteCell({ aiAgent, isFavorite, toggleFavorite }: FavoriteCellProps) {
  const [isHoveringPin, setIsHoveringPin] = useState(false);
  const { t } = useTranslation();

  const tooltipContent = isFavorite ? t('common.unpin') : t('common.pin');

  const handleMouseEnter = useCallback(() => {
    setIsHoveringPin(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHoveringPin(false);
  }, []);

  const handleToggleFavorite = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(e, aiAgent.brain_id);
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        !isFavorite &&
          'opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100'
      )}
    >
      <InfoTooltip content={tooltipContent} side="top">
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          onClick={handleToggleFavorite}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isFavorite ? (
            isHoveringPin ? (
              <PinOffIcon size={16} />
            ) : (
              <PinFilledIcon size={16} />
            )
          ) : isHoveringPin ? (
            <PinFilledIcon size={16} />
          ) : (
            <PinIcon size={16} />
          )}
          <span className="sr-only">{tooltipContent}</span>
        </Button>
      </InfoTooltip>
    </div>
  );
}
